import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { STATUS_BADGE_CLASS, TRAINING_REQUESTS } from "./trainingData";
import "./training.css";

/* My Training Requests — ported from the prototype (training.html).
   Cancel is only offered on Pending Approval rows (with confirmation modal);
   Evaluate is only offered on Completed rows. */

const TrainingListPage = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [yearFilter, setYearFilter] = useState("2026");
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelledIds, setCancelledIds] = useState(() => new Set());

  const rows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return TRAINING_REQUESTS.map((row) =>
      cancelledIds.has(row.id) ? { ...row, status: "Cancelled" } : row
    ).filter((row) => {
      if (yearFilter !== "all" && row.year !== yearFilter) return false;
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (!query) return true;
      return [row.id, row.course, row.provider].some((value) => value.toLowerCase().includes(query));
    });
  }, [search, statusFilter, yearFilter, cancelledIds]);

  const closeCancelModal = () => setCancelTarget(null);

  const confirmCancel = () => {
    if (!cancelTarget) return;
    setCancelledIds((prev) => {
      const next = new Set(prev);
      next.add(cancelTarget);
      return next;
    });
    closeCancelModal();
  };

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeCancelModal();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <div className="bes-training">
      <div className="training-page-head">
        <div className="title-left">
          <div className="page-icon training-icon">
            <i className="ri-graduation-cap-fill" />
          </div>
          <div>
            <h1>My Training Requests</h1>
            <p>Track and manage your professional development and course applications.</p>
          </div>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => navigate("/training/request/create")}>
          <i className="ri-add-line" /> New Training Request
        </button>
      </div>

      <div className="data-container">
        <div className="filters-standard">
          <div className="filter-group search">
            <i className="ri-search-line" />
            <input
              className="form-control"
              type="text"
              placeholder="Search by course name, provider, or ID..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <div className="filter-group">
            <select className="form-control" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <option value="all">All Statuses</option>
              <option value="Pending Approval">Pending Approval</option>
              <option value="Approved">Approved</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>
          <div className="filter-group">
            <select className="form-control" value={yearFilter} onChange={(event) => setYearFilter(event.target.value)}>
              <option value="2026">2026</option>
              <option value="2025">2025</option>
              <option value="2024">2024</option>
            </select>
          </div>
        </div>

        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th>Request ID</th>
                <th className="w-35">Course Details</th>
                <th>Training Dates</th>
                <th>Est. Cost</th>
                <th>Status</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className={row.status === "Cancelled" ? "request-cancelled-row" : ""}>
                  <td className="font-medium">#{row.id}</td>
                  <td>
                    <div className="course-name">{row.course}</div>
                    <div className="course-provider">{row.provider}</div>
                  </td>
                  <td>{row.dates}</td>
                  <td className="font-medium">{row.cost}</td>
                  <td>
                    <span className={`status-badge ${STATUS_BADGE_CLASS[row.status] || "status-cancelled"}`}>
                      {row.status}
                    </span>
                  </td>
                  <td className="text-center">
                    <div className="actions-cell">
                      <button
                        type="button"
                        className="action-btn"
                        title="View Details"
                        onClick={() => navigate(`/training/request/${row.id}`)}
                      >
                        <i className="ri-eye-line" />
                      </button>
                      {row.status === "Pending Approval" && (
                        <button
                          type="button"
                          className="action-btn delete-btn"
                          title="Cancel Request"
                          onClick={() => setCancelTarget(row.id)}
                        >
                          <i className="ri-close-line" />
                        </button>
                      )}
                      {row.status === "Completed" && (
                        <button
                          type="button"
                          className="evaluate-btn"
                          title="Evaluate Training"
                          onClick={() => navigate(`/training/request/${row.id}/evaluate`)}
                        >
                          <i className="ri-survey-line" /> Evaluate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-muted empty-row">
                    No training requests match the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="pagination-container">
          <span className="showing-text text-muted">
            Showing {rows.length ? 1 : 0} to {rows.length} of {rows.length} entries
          </span>
          <div className="pagination">
            <button type="button" className="page-btn" disabled>
              <i className="ri-arrow-left-double-line" />
            </button>
            <button type="button" className="page-btn" disabled>
              <i className="ri-arrow-left-s-line" />
            </button>
            <button type="button" className="page-btn active">
              1
            </button>
            <button type="button" className="page-btn" disabled>
              <i className="ri-arrow-right-s-line" />
            </button>
            <button type="button" className="page-btn" disabled>
              <i className="ri-arrow-right-double-line" />
            </button>
          </div>
        </div>
      </div>

      {cancelTarget && (
        <div
          className="cancel-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cancelModalTitle"
          onClick={(event) => {
            if (event.target === event.currentTarget) closeCancelModal();
          }}
        >
          <div className="cancel-modal-box">
            <div className="cancel-modal-header">
              <h3 id="cancelModalTitle">Cancel Training Request</h3>
            </div>
            <div className="cancel-modal-body">
              <p>Are you sure you want to cancel this Training Request?</p>
              <p className="cancel-request-label">Request ID: #{cancelTarget}</p>
            </div>
            <div className="cancel-modal-actions">
              <button type="button" className="btn btn-outline" onClick={closeCancelModal}>
                No
              </button>
              <button type="button" className="btn btn-primary" onClick={confirmCancel}>
                Yes, Cancel Request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingListPage;
